<template>
  <div
    :itemprop="SCHEMA_ITEM_PROP.OFFERS"
    itemscope
    :itemtype="SCHEMA_ITEM_TYPE.OFFER"
    class="bg-EXPONDO-grey-5 p-4 rounded flex flex-col gap-4"
  >
    <ProductCardBadgeWrapper
      :attributes-localized="attributesLocalized"
    />
    <EnergyRating
      v-if="hasEnergyRating"
      :energy-rating="attributesLocalized.energyRating"
      class="lg:hidden"
    />
    <div class="flex flex-col gap-1">
      <VatInfo
        class="text-EXPONDO-black leading-4"
        :vat-percentage="product.vatPercentage"
        :is-net-invoice-enabled="isChangeTaxRateEnabled"
        :product-price="product.price"
      />
      <ProductSummaryDetails
        v-if="product"
        :product="product"
        is-active-product
      />
      <LimitedTimeOffer v-if="isDiscounted" />
      <SectionTransition
        v-if="isLoyaltyDiscountEnabled"
        :has-content="!personalizedDiscountLoading"
        reserve-height="86px"
        client-only
        data-testid="personalized-discount"
      >
        <PersonalizedDiscount :product-price="finalPrice" />
      </SectionTransition>
      <PriceDisclaimer
        :is-discounted="isDiscounted"
        :last30-days-price="last30DaysPrice"
      />
    </div>
    <SectionTransition :has-content="isGroupActive(volumeDiscountGroup)">
      <p class="font-bold text-sm">
        {{ $t('Volume discount') }}
      </p>
      <VolumeDiscounts
        :final-price="finalPrice"
        :volume-discounts="volumeDiscountConfigurations[volumeDiscountGroup]"
        :loyalty-discount-percentage="personalizedDiscount"
        :product-sku="product.sku"
        :stock="stock"
        :loading-stocks="loadingStocks"
      />
    </SectionTransition>

    <SectionTransition
      :has-content="!loadingStocks"
      reserve-height="212px"
    >
      <AddToCart
        :id="MAIN_ADD_TO_CART_ID"
        :product="product"
        :product-adjusted-for-cart="productAdjustedForCart"
        :has-in-stock-alternative-products="hasInStockAlternativeProducts"
      />
    </SectionTransition>

          <BilliePaymentInfo v-if="showBilliePaymentInfo" />

    <SectionTransition :has-content="showExtraGuaranteeInfo">
      <ExtraGuaranteeInfo />
    </SectionTransition>
    <div class="flex flex-col gap-1">
      <StockInformation
        :full-product-price="fullProductPrice"
        :stock-count="stock"
        :is-in-stock="isInStock"
        :is-test-product="testProductIdentifier"
      />
      <ProductCardDelivery
        v-if="showDeliveryTiming"
        :delivery-date="deliveryDate"
        :availability-date="availabilityDate"
      />
      <LowestPriceGuaranteed
        :price-guarantee-url="priceGuaranteeUrl"
      />
    </div>
    <ClientOnly>
      <ABWareAlternative
        v-if="product.sku"
        :sku="product.sku"
      />
    </ClientOnly>
  </div>
</template>

<script lang="ts">

import { defineAsyncComponent } from 'vue';
import type { ProductVariant } from '~~/types/commercetools';
import {
  getProductAttributesExtended,
} from '~~/helpers/product/getProductAttributesExtended';
import { SCHEMA_ITEM_PROP, SCHEMA_ITEM_TYPE } from '~~/constants/seo';
import type { ProductProcessedAttributes } from '~~/types/product/attribute/ProductProcessedAttributes';
import { PRODUCT_ATTRIBUTES } from '~~/constants/products';
import { isTestProduct } from '~~/helpers/product/isTestProduct';
import isString from '~~/helpers/dataStructure/isString';
import { MAIN_ADD_TO_CART_ID } from '~~/constants/floatingAddToCart';
import { DEFAULT_VOLUME_DISCOUNT_GROUP } from '~~/constants/volumeDiscounts';
import { BILLIE_PAYMENT_INFO } from '~~/constants/paymentMethods';

export default defineComponent({
  name: 'ProductPricingSection',
  components: {

    ProductCardBadgeWrapper: defineAsyncComponent(() => import('~/components/organisms/Product/ProductCardBadgeWrapper.vue')),

    PriceDisclaimer: defineAsyncComponent(() => import('~/components/molecules/Product/PriceDisclaimer.vue')),

    VatInfo: defineAsyncComponent(() => import('~/components/atoms/Product/VatInfo.vue')),

    EnergyRating: defineAsyncComponent(() => import(/* webpackChunkName: "EnergyRating" */
      '~/components/molecules/Product/EnergyRating.vue')),

    ProductSummaryDetails: defineAsyncComponent(() => import(/* webpackChunkName: "ProductSummaryDetails" */
      '~/components/organisms/Product/ProductSummaryDetails.vue')),

    StockInformation: defineAsyncComponent(() => import('~/components/molecules/Product/StockInformation.vue')),

    ProductCardDelivery: defineAsyncComponent(() => import(/* webpackChunkName: "ProductCardDelivery" */
      '~/components/atoms/Product/ProductCardDelivery.vue')),

    LowestPriceGuaranteed: defineAsyncComponent(() => import('~/components/atoms/Product/LowestPriceGuaranteed.vue')),

    LimitedTimeOffer: defineAsyncComponent(() => import(/* webpackChunkName: "LimitedTimeOffer" */
      '~/components/atoms/Product/LimitedTimeOffer.vue')),

    ExtraGuaranteeInfo: defineAsyncComponent(() => import(/* webpackChunkName: "ExtraGuaranteeInfo" */
      '~/components/atoms/ExtraGuaranteeInfo/ExtraGuaranteeInfo.vue')),

    BilliePaymentInfo: defineAsyncComponent(() => import(/* webpackChunkName: "BilliePaymentInfo" */
      '~/components/atoms/BilliePaymentInfo/BilliePaymentInfo.vue')),

    PersonalizedDiscount: defineAsyncComponent(() => import(/* webpackChunkName: "PersonalizedDiscount" */
      '~/components/molecules/Product/PersonalizedDiscount/PersonalizedDiscount.vue')),

  },
  props: {
    product: {
      type: Object as PropType<ProductVariant>,
      required: true,
    },
    attributesLocalized: {
      type: Object as PropType<ProductProcessedAttributes>,
      required: true,
    },
    productAdjustedForCart: {
      type: Object,
      required: true,
    },
    availableQuantity: {
      type: Number,
      required: true,
    },
    stock: {
      type: Number,
      required: true,
    },
    loadingStocks: {
      type: Boolean,
      required: true,
    },
    hasInStockAlternativeProducts: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {

    const { product } = toRefs(props);

    const {
      load: loadLoyaltyDiscount,
      personalizedDiscountLoading,
      hasPersonalizedDiscount,
      personalizedDiscount,
    } = usePersonalizedDiscount();

    const { getPriceInformation } = useProductPrice();
    const { isChangeTaxRateEnabled, isLoyaltyDiscountEnabled } = useStoreVsfExtended();
    const { countryCode } = useLocalization();

    const isProductReadyToShip = computed(() => props.availableQuantity > 0);

    const {
      isFreightFeeApplied,
      isParcelFeeApplied,
      shippingFeeDetails,
      deliveryDate,
    } = useProductShipping(product);

    const {
      fullProductPrice,
      finalPrice,
      isDiscounted,
      last30DaysPrice,
    } = getPriceInformation(product);
    const {
      isGroupActive,
      load: loadVolumeDiscount,
      volumeDiscountConfigurations,
    } = useVolumeDiscountsStore();

    const releaseDate = computed(() =>
      getProductAttributesExtended(
        props.product,
        {
          attributeNames: [PRODUCT_ATTRIBUTES.RELEASE_DATE],
        },
      )[PRODUCT_ATTRIBUTES.RELEASE_DATE]);

    const testProductIdentifier = computed(() => isTestProduct(product.value.sku));
    const releaseDateString = computed(() => isString(releaseDate.value) ? releaseDate.value : '');

    const { calculateAvailabilityDate } = useProductAvailabilityDate(releaseDateString.value);
    const availabilityDate = computed(() => testProductIdentifier.value ? calculateAvailabilityDate() : '');

    const { priceGuaranteeUrl } = useRouteBestPriceGuarantee();

    const isInStock = computed(() => props.stock > 0);
    const showDeliveryTiming = computed(() => (deliveryDate.value && isInStock.value) || testProductIdentifier.value);
    const hasEnergyRating = computed(() => props.attributesLocalized?.energyRating);
    const volumeDiscountGroup = computed(() =>
      getProductAttributesExtended(
        props.product,
        { attributeNames: [PRODUCT_ATTRIBUTES.VOLUME_DISCOUNT_GROUP] },
      )[PRODUCT_ATTRIBUTES.VOLUME_DISCOUNT_GROUP] || DEFAULT_VOLUME_DISCOUNT_GROUP,
    );

    const { isGuaranteeExcluded } = useExtraGuaranteeExcluded();
    const showExtraGuaranteeInfo = computed(() => {

      return isInStock.value && !isGuaranteeExcluded(product.value);

    });

    const showBilliePaymentInfo = computed(() => {

      return BILLIE_PAYMENT_INFO[countryCode.value];

    });

    useAsyncData(`volume-discount-${volumeDiscountGroup.value}`, async () => {

      await loadVolumeDiscount(volumeDiscountGroup.value);

      return null;

    });

    onMounted(() => {

      loadLoyaltyDiscount();

    });

    return {
      SCHEMA_ITEM_TYPE,
      SCHEMA_ITEM_PROP,
      isFreightFeeApplied,
      isParcelFeeApplied,
      shippingFeeDetails,
      isProductReadyToShip,
      deliveryDate,
      fullProductPrice,
      finalPrice,
      isDiscounted,
      last30DaysPrice,
      isChangeTaxRateEnabled,
      isGroupActive,
      availabilityDate,
      isInStock,
      testProductIdentifier,
      showDeliveryTiming,
      priceGuaranteeUrl,
      hasEnergyRating,
      MAIN_ADD_TO_CART_ID,
      volumeDiscountConfigurations,
      volumeDiscountGroup,
      showExtraGuaranteeInfo,
      isLoyaltyDiscountEnabled,
      personalizedDiscountLoading,
      showBilliePaymentInfo,
      hasPersonalizedDiscount,
      personalizedDiscount,
    };

  },
});

</script>
